import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ScoringDashboard from '../components/ScoringDashboard';
import ShareDialog from '../components/ShareDialog';
import CollaboratorBar from '../components/CollaboratorBar';
import { getAudit, getAudits, saveAudit, deleteAudit } from '../utils/storage';
import { calculateNextIndex, getScoreColor, getTier } from '../utils/scoring';
import { formatDate } from '../utils/export';
import { joinAuditSession, broadcastUpdate, leaveAuditSession } from '../utils/realtime';
import { supabase } from '../utils/supabase';
import ExecutiveSummary from '../components/ExecutiveSummary';
import WhiteLabelSettings from '../components/WhiteLabelSettings';
import { exportReportToPDF } from '../utils/export';

export default function Dashboard() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [audits, setAudits] = useState([]);
  const [previousAudit, setPreviousAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserName, setCurrentUserName] = useState(null);
  const [remoteUpdate, setRemoteUpdate] = useState(null);
  const [toast, setToast] = useState(null);
  const [showExecSummary, setShowExecSummary] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, 'saving', 'saved'

  useEffect(() => {
    async function load() {
      setLoading(true);
      if (auditId) {
        const a = await getAudit(auditId);
        if (a && a.id) {
          setAudit(a);
          if (a.previousAuditId) {
            const prev = await getAudit(a.previousAuditId);
            setPreviousAudit(prev);
          }
        } else {
          setAudit(null);
        }
      } else {
        const all = await getAudits();
        setAudits(all);
        setAudit(null);
      }
      setLoading(false);
    }
    load();
  }, [auditId, navigate]);

  // Real-time collaboration
  useEffect(() => {
    if (!auditId) { leaveAuditSession(); return; }

    async function setupRealtime() {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id || 'anon_' + Date.now();
      const userName = user?.user_metadata?.full_name || user?.email || 'Unknown';
      setCurrentUserId(userId);
      setCurrentUserName(userName);

      joinAuditSession(
        auditId,
        userId,
        userName,
        (remoteAudit, remoteName) => {
          setRemoteUpdate({ audit: remoteAudit, from: remoteName });
        },
        (users) => setCollaborators(users),
        (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); }
      );
    }

    setupRealtime();
    return () => leaveAuditSession();
  }, [auditId]);

  // Apply remote updates — merge lightweight changes into current audit
  useEffect(() => {
    if (remoteUpdate && audit) {
      const merged = { ...audit };
      const remote = remoteUpdate.audit;
      // Merge only the fields that were broadcast
      if (remote.scores) merged.scores = remote.scores;
      if (remote.memoryTest) merged.memoryTest = remote.memoryTest;
      if (remote.socialProof) merged.socialProof = remote.socialProof;
      if (remote.brandCoherence) merged.brandCoherence = remote.brandCoherence;
      if (remote.revenueLeaks) merged.revenueLeaks = remote.revenueLeaks;
      if (remote.menuAnalysis) merged.menuAnalysis = remote.menuAnalysis;
      if (remote.beverageAnalysis) merged.beverageAnalysis = remote.beverageAnalysis;
      if (remote.comments) merged.comments = remote.comments;
      if (remote.teamScores) merged.teamScores = remote.teamScores;
      if (remote.roadmapProgress) merged.roadmapProgress = remote.roadmapProgress;
      if (remote.roadmapComments) merged.roadmapComments = remote.roadmapComments;
      if (remote.approvedCompetitors) merged.approvedCompetitors = remote.approvedCompetitors;
      if (remote.marketData) merged.marketData = { ...merged.marketData, ...remote.marketData };
      setAudit(merged);
      setRemoteUpdate(null);
    }
  }, [remoteUpdate]);

  // Debounced save — waits 500ms after last change before saving to DB
  const saveTimeoutRef = useRef(null);
  const handleUpdate = (updatedAudit, fieldName) => {
    setAudit({ ...updatedAudit });
    // Debounce the DB save
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    setSaveStatus('saving');
    saveTimeoutRef.current = setTimeout(() => {
      saveAudit(updatedAudit)
        .then(() => { setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); })
        .catch(err => { console.warn('Background save failed:', err.message); setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); });
    }, 500);
    // Broadcast immediately for real-time collab
    broadcastUpdate(updatedAudit, currentUserId, currentUserName, fieldName);
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = (id) => {
    console.log('Delete clicked for:', id);
    setDeleteConfirm(id);
  };

  const confirmDelete = () => {
    const id = deleteConfirm;
    console.log('Delete confirmed:', id);
    setDeleteConfirm(null);
    setAudits(prev => prev.filter(a => a.id !== id));
    setAudit(null);
    deleteAudit(id).catch(err => console.warn('Delete failed:', err.message));
    try {
      const local = JSON.parse(localStorage.getItem('whats_next_audits') || '[]');
      localStorage.setItem('whats_next_audits', JSON.stringify(local.filter(a => a.id !== id)));
    } catch(e) {}
  };

  const deleteDialog = deleteConfirm && (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={() => setDeleteConfirm(null)}>
      <div className="card p-6 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
        <h3 className="font-display text-lg text-white mb-2">Delete Audit?</h3>
        <p className="text-neutral-400 text-sm mb-5">This cannot be undone.</p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => setDeleteConfirm(null)} className="px-5 py-2 rounded-lg bg-neutral-800 text-neutral-300 text-sm hover:bg-neutral-700">Cancel</button>
          <button onClick={confirmDelete} className="px-5 py-2 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 font-semibold">Delete</button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="text-center py-20 text-neutral-500">Loading...</div>;
  }

  // Audit list view
  if (!auditId) {
    if (audits.length === 0) {
      return (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-neutral-800/50 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📋</span>
          </div>
          <h2 className="font-display text-xl text-white mb-2">No Audits Yet</h2>
          <p className="text-sm text-neutral-400 mb-6">Start your first business health audit</p>
          <Link to="/new" className="btn-primary">Start New Audit</Link>
        </div>
      );
    }

    // Group audits by concept name
    const grouped = {};
    audits.forEach(a => {
      const name = a.intake?.conceptName || 'Unknown';
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push(a);
    });

    return (
      <div>
        {deleteDialog}
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-2xl text-white">All Audits</h1>
          <Link to="/new" className="btn-primary text-sm">+ New Audit</Link>
        </div>

        {Object.entries(grouped).map(([name, groupAudits]) => {
          const sorted = groupAudits.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          const hasMultiple = sorted.length > 1;

          return (
            <div key={name} className="mb-6">
              {hasMultiple && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-brand-gold font-semibold">{sorted.length} audits</span>
                  <span className="text-xs text-neutral-500">— Score history available</span>
                </div>
              )}
              <div className="space-y-2">
                {sorted.map((a, idx) => {
                  const index = calculateNextIndex(a.scores || {});
                  const color = getScoreColor(index);
                  const tier = getTier(index);
                  const prevAudit = idx < sorted.length - 1 ? sorted[idx + 1] : null;
                  const prevIndex = prevAudit ? calculateNextIndex(prevAudit.scores || {}) : null;
                  const delta = prevIndex != null ? index - prevIndex : null;

                  return (
                    <div key={a.id} className="card p-4 hover:border-brand-gold/30 transition-all">
                      <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate(`/dashboard/${a.id}`)}>
                        <div className={`w-12 h-12 rounded-lg ${color.bg} flex items-center justify-center flex-shrink-0`}>
                          <span className={`text-lg font-black ${color.text}`}>{index.toFixed(1)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-white truncate">{a.intake?.conceptName}</p>
                            <span className={`text-[9px] px-2 py-0.5 rounded font-medium flex-shrink-0 ${
                              a.status === 'delivered' ? 'bg-emerald-500/15 text-emerald-400' :
                              a.status === 'review' ? 'bg-amber-500/15 text-amber-400' :
                              a.status === 'in_progress' ? 'bg-blue-500/15 text-blue-400' :
                              a.status === 'follow_up' ? 'bg-purple-500/15 text-purple-400' :
                              'bg-neutral-800 text-neutral-500'
                            }`}>
                              {a.status === 'in_progress' ? 'In Progress' : a.status === 'follow_up' ? 'Follow-Up' : a.status === 'review' ? 'In Review' : (a.status || 'Draft').charAt(0).toUpperCase() + (a.status || 'draft').slice(1)}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-500">
                            {a.intake?.city} — {formatDate(a.createdAt)}
                          </p>
                        </div>
                        {delta != null && (
                          <span className={`text-xs font-bold ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-neutral-400'}`}>
                            {delta > 0 ? '↑' : delta < 0 ? '↓' : '='}{Math.abs(delta).toFixed(1)}
                          </span>
                        )}
                        <span className="text-xs px-2 py-1 rounded-full font-medium hidden sm:inline-block"
                          style={{ backgroundColor: tier.color + '20', color: tier.color }}>
                          {tier.name}
                        </span>
                      </div>
                      <div className="flex gap-2 mt-2 ml-16">
                        <button onClick={() => navigate(`/new?reaudit=${a.id}`)} className="text-[11px] px-3 py-1 rounded bg-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-700 transition-all">
                          Re-audit
                        </button>
                        <button onClick={() => handleDelete(a.id)} className="text-[11px] px-3 py-1 rounded bg-neutral-800 text-red-400 hover:bg-red-500/20 transition-all">
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Single audit dashboard
  if (!audit) return (
    <div className="text-center py-20">
      <p className="text-neutral-400 mb-4">Audit not found.</p>
      <button onClick={() => navigate('/dashboard')} className="btn-primary text-sm">Back to Dashboard</button>
    </div>
  );

  return (
    <div>
      {/* Action bar — responsive */}
      <div className="mb-6 space-y-3 sm:space-y-0">
        {/* Top row: back, save status, audit status */}
        <div className="flex items-center gap-2 mb-2 sm:mb-0">
          <Link to="/dashboard" className="btn-ghost text-xs py-2">← All</Link>
          {saveStatus && (
            <span className={`text-[10px] px-2 py-1 rounded transition-all ${saveStatus === 'saving' ? 'text-amber-400 bg-amber-400/10' : 'text-emerald-400 bg-emerald-400/10'}`}>
              {saveStatus === 'saving' ? 'Saving...' : 'Saved'}
            </span>
          )}
          <select
            value={audit.status || 'draft'}
            onChange={e => {
              // Cancel any pending debounced save to prevent overwrite
              if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
              const newStatus = e.target.value;
              console.log('STATUS CHANGE:', audit.status, '->', newStatus);
              const updated = { ...audit, status: newStatus };
              setAudit(updated);
              setSaveStatus('saving');
              saveAudit(updated, `Status changed to ${newStatus}`)
                .then((result) => {
                  console.log('STATUS SAVED, returned:', result?.status);
                  // Re-set audit with the correct status in case save returned stale data
                  setAudit(prev => ({ ...prev, status: newStatus }));
                  setSaveStatus('saved');
                  setTimeout(() => setSaveStatus(null), 2000);
                })
                .catch(() => { setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); });
            }}
            className={`text-xs px-3 py-2 sm:py-1.5 rounded-lg font-medium border-0 ${
              audit.status === 'delivered' ? 'bg-emerald-500/10 text-emerald-400' :
              audit.status === 'review' ? 'bg-amber-500/10 text-amber-400' :
              audit.status === 'in_progress' ? 'bg-blue-500/10 text-blue-400' :
              audit.status === 'follow_up' ? 'bg-purple-500/10 text-purple-400' :
              'bg-neutral-800 text-neutral-400'
            }`}
          >
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="review">In Review</option>
            <option value="delivered">Delivered</option>
            <option value="follow_up">Follow-Up</option>
          </select>
          <div className="flex-1" />
          <Link to={`/new?reaudit=${audit.id}`} className="btn-primary text-xs py-2">Re-Audit</Link>
        </div>
        {/* Action buttons — scroll on mobile */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
          <Link to={`/report/${audit.id}`} className="btn-secondary text-xs py-2 whitespace-nowrap">View Report</Link>
          <a href={`/client/${audit.id}`} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs py-2 whitespace-nowrap">Client View</a>
          <button onClick={() => setShowExecSummary(true)} className="btn-secondary text-xs py-2 whitespace-nowrap">Exec Summary</button>
          <button onClick={() => setShowShare(true)} className="btn-secondary text-xs py-2 whitespace-nowrap">Share</button>
        </div>
      </div>

      {/* Executive Summary Modal */}
      {showExecSummary && (
        <div className="fixed inset-0 bg-black/90 z-50 overflow-auto flex flex-col items-center py-4 sm:py-8 px-2 sm:px-4" onClick={() => setShowExecSummary(false)}>
          <div className="mb-4 flex gap-3">
            <button
              onClick={async (e) => {
                e.stopPropagation();
                await exportReportToPDF('exec-summary', `WhatsNEXT-ExecSummary-${audit.intake?.conceptName || 'Report'}.pdf`);
              }}
              className="btn-primary text-xs py-2"
            >
              Download PDF
            </button>
            <button onClick={() => setShowExecSummary(false)} className="btn-ghost text-xs">Close</button>
          </div>
          <div onClick={(e) => e.stopPropagation()} className="shadow-2xl rounded-lg overflow-auto max-h-[85vh] max-w-full">
            <ExecutiveSummary audit={audit} />
          </div>
        </div>
      )}

      {showShare && (
        <ShareDialog
          auditId={audit.id}
          conceptName={audit.intake?.conceptName || 'Report'}
          onClose={() => setShowShare(false)}
        />
      )}

      <CollaboratorBar users={collaborators} currentUserId={currentUserId} />

      {/* Toast notifications */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 bg-neutral-900 border border-brand-gold/30 rounded-lg px-4 py-3 shadow-xl animate-fade-in-up max-w-sm">
          <p className="text-xs text-neutral-300">{toast}</p>
        </div>
      )}

      <ScoringDashboard
        audit={audit}
        onUpdate={handleUpdate}
        previousAudit={previousAudit}
      />

      {/* White Label & Activity */}
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <WhiteLabelSettings audit={audit} onUpdate={handleUpdate} />

      {/* Team Activity Log */}
      {audit.editLog?.length > 0 ? (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-white mb-3">Activity Log</h3>
          <div className="max-h-48 overflow-y-auto space-y-1.5">
            {[...audit.editLog].reverse().slice(0, 50).map((entry, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px]">
                <span className="text-brand-gold font-medium w-20 flex-shrink-0 truncate">{entry.user || 'Unknown'}</span>
                <span className="text-neutral-600 w-28 flex-shrink-0">{new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                <span className="text-neutral-400">{entry.action}</span>
              </div>
            ))}
          </div>
        </div>
      ) : <div />}
      </div>
    </div>
  );
}
